'use client';
import { useState, useEffect } from 'react';

// Single shared avatar — shows the user's photo (Google login or manual
// upload) when available, falling back to initials otherwise. Used
// everywhere a user avatar appears (header, account pages, feedback, etc.)
// so photo-vs-initials logic lives in exactly one place.

const getInitials = ({ firstName, lastName, name, email } = {}) => {
    const first = firstName?.[0] || name?.[0];
    const last = lastName?.[0];
    if (first && last) return (first + last).toUpperCase();
    if (first) return first.toUpperCase();
    if (email) return email[0].toUpperCase();
    return '?';
};

export const UserAvatar = ({
    firstName, lastName, name, email, image,
    size = 32, className = '', style = {},
}) => {
    const [imgFailed, setImgFailed] = useState(false);

    // Reset the failed-load flag whenever the photo URL itself changes
    // (e.g. once a fresher one comes back from a profile refetch).
    useEffect(() => { setImgFailed(false); }, [image]);

    const initials = getInitials({ firstName, lastName, name, email });

    if (image && !imgFailed) {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={image}
                alt=""
                // Google's avatar CDN (lh3.googleusercontent.com) intermittently
                // rejects the request based on the Referer header — most
                // noticeable right after a brand-new account's first login.
                // Omitting the referrer avoids that entirely.
                referrerPolicy="no-referrer"
                onError={() => setImgFailed(true)}
                className={`border-circle flex-shrink-0 ${className}`}
                style={{ width: size, height: size, objectFit: 'cover', ...style }}
            />
        );
    }

    return (
        <div
            className={`flex align-items-center justify-content-center border-circle bg-primary text-white font-bold flex-shrink-0 ${className}`}
            style={{ width: size, height: size, fontSize: size * 0.4, userSelect: 'none', ...style }}
        >
            {initials}
        </div>
    );
};
